<template>
  <multiselect
    id="locationSearch" class="location-select multiselect-rlnx" v-model="location" label="name" track-by="id" placeholder="Type to search" open-direction="bottom"
    :options="options" :searchable="true" :loading="$apollo.queries.options.loading" :internal-search="$root.isGWR" :close-on-select="true"
    :max-height="600" :show-no-results="false" :hide-selected="false" :preserve-search="true" :show-labels="false" @search-change="onSearch" :multiple="multiple">
    <template v-slot:option="{ option }">
      <div class="option-container">
        <span class="option-title">{{ option.name }}</span>
        <span class="option-description" :class="isRemoveLabel(option) ? 'remove-label' : ''">
          <template v-if="isRemoveLabel(option)">Click to remove</template>
          <template v-else>{{ option.description }}<template v-if="option.countryCode"> {{ toFlag(option.countryCode) }}</template></template>
        </span>
      </div>
    </template>
    <template v-slot:noOptions>
      E.g. London, New York, San Francisco
    </template>
  </multiselect>
</template>

<script>
import flag from 'country-code-emoji';
import Multiselect from 'vue-multiselect';
import LocationsIndexQuery from '@/graphql/queries/LocationsIndex.gql';
import LocationsSuggestQuery from '@/graphql/queries/LocationsSuggest.gql';

export default {
  components: { Multiselect },
  props: {
    value: {
      default() {
        return this.multiple ? [] : {};
      },
    },
    onlyCities: {
      type: Boolean,
      default: () => false,
    },
    multiple: {
      type: Boolean,
      default: () => false,
    },
  },
  data: () => ({
    location: null,
    searchName: null,
    searchNameTimeout: null,
    options: [],
  }),
  created() {
    if (this.$root.isGWR) {
      this.location = this.value;
      this.options = GWROptions;
    }
  },
  apollo: {
    onload: {
      query: LocationsIndexQuery,
      variables() {

        if (!this.multiple) {
          const { id } = this.value;
          return { where: { id } };
        }
        // @TODO: ElasticSearch throws an error if id_in is used
        const ids = Array.isArray(this.value) ? this.value.map(({ id }) => ({ id })) : [];
        return { where: { OR: ids } };
      },
      manual: true,
      result({ data: { locations } = {} } = {}) {
        if (!this.multiple && locations.length) {
          this.location = locations[0] || {};
          this.options = [ this.location ];
        } else if (this.multiple && locations.length) {
          this.location = locations;
          this.options = this.location;
          console.log('OPTIONS: ', this.options);
        }
      },
      skip() {
        return this.searchName || !this.hasValue || this.$root.isGWR;
      },
      fetchPolicy: 'network-only',
    },
    options: {
      query: LocationsSuggestQuery,
      variables() {
        const { where } = this;
        return { where };
      },
      update: ({ locationsSuggest }) => locationsSuggest || [],
      skip() {
        return !this.searchName || this.searchName.length === 0 || this.$root.isGWR;
      },
      fetchPolicy: 'network-only',
    },
  },
  computed: {
    where() {
      const where = {
        name: this.searchName || '',
        featureCodes: [ 'ADM1', 'ADM2', 'ADM3', 'ADMD', 'RGN', 'PPL', 'PPLS', 'PPLC', 'PPLA', 'PPLA2', 'PPLA3', 'PPLA4', 'PPLA5', 'PPLF', 'PPLX' ],
      };
      if (this.onlyCities) {
        where.featureCodes = [ 'PPL', 'PPLS', 'PPLC', 'PPLA', 'PPLA2', 'PPLA3', 'PPLA4', 'PPLA5', 'PPLF', 'PPLX' ];
      }
      return where;
    },
    hasValue() {
      return this.multiple ? (Array.isArray(this.value) && this.value.length) : this.value;
    },
  },
  methods: {
    onSearch(val) {
      clearTimeout(this.searchNameTimeout);
      this.searchNameTimeout = setTimeout(() => {
        this.searchName = val;
      }, 500);
    },
    toFlag: countryCode => flag(countryCode),
    isRemoveLabel(option) {
      if (!this.multiple) {
        return this.value && option.id === this.value.id;
      } else if (this.multiple && Array.isArray(this.value)) {
        return this.value.map(({ id }) => id).includes(option.id);
      }
      return false;
    }
  },
  watch: {
    location() {
      if (!this.multiple) {
        const { id: existingID } = this.value || {};
        const { id: newID } = this.location || {};
        if (existingID !== newID) {
          this.$emit('input', this.location);
        }
      } else {
        const existingIDs = Array.isArray(this.value) ? this.value.map(({ id }) => id) : [];
        const newIDs = Array.isArray(this.location) ? this.location.map(({ id }) => id) : [];
        if (JSON.stringify(existingIDs) !== JSON.stringify(newIDs)) {
          this.$emit('input', this.location);
        }
      }
    },
    value() {
      console.log('VALUE FROM WATCHER: ', this.value);
      if (this.$root.isGWR) {
        console.log('VALUE FROM WATCHER: ', this.value);
        this.location = this.value;
      }
    },
  },
};

export const GWROptions = [
  { name: 'Barnstaple Station', id: 'GWR-1' },
  { name: 'Basingstoke Station', id: 'GWR-2' },
  { name: 'Bath Spa Station', id: 'GWR-3' },
  { name: 'Bodmin Parkway Station', id: 'GWR-4' },
  { name: 'Bourne End Station', id: 'GWR-5' },
  { name: 'Bradford-on-Avon Station', id: 'GWR-6' },
  { name: 'Bramley Station', id: 'GWR-7' },
  { name: 'Bridgwater Station', id: 'GWR-8' },
  { name: 'Bristol Parkway Station', id: 'GWR-9' },
  { name: 'Bristol Parkway Train Crew Depot', id: 'GWR-10' },
  { name: 'Bristol Temple Meads Station', id: 'GWR-11' },
  { name: 'Camborne Station', id: 'GWR-12' },
  { name: 'Cardiff Central Station', id: 'GWR-13' },
  { name: 'Castle Cary Station', id: 'GWR-14' },
  { name: 'Charlbury Station', id: 'GWR-15' },
  { name: 'Cheltenham Spa Station', id: 'GWR-16' },
  { name: 'Chippenham Station', id: 'GWR-17' },
  { name: 'Cholsey Station', id: 'GWR-18' },
  { name: 'Cookham Station', id: 'GWR-19' },
  { name: 'Dawlish Station', id: 'GWR-20' },
  { name: 'Didcot Parkway Station', id: 'GWR-21' },
  { name: 'Driver Training Academy', id: 'GWR-22' },
  { name: 'Evesham Station', id: 'GWR-23' },
  { name: 'Exeter Central Station', id: 'GWR-24' },
  { name: 'Exeter Depot', id: 'GWR-25' },
  { name: 'Exeter St Davids Station', id: 'GWR-26' },
  { name: 'Exmouth Station', id: 'GWR-27' },
  { name: 'Fratton Station', id: 'GWR-28' },
  { name: 'Frome Station', id: 'GWR-29' },
  { name: 'Gloucester Station', id: 'GWR-30' },
  { name: 'Henley-On-Thames Station', id: 'GWR-31' },
  { name: 'Kemble Station', id: 'GWR-32' },
  { name: 'Keynsham Station', id: 'GWR-33' },
  { name: 'Kingham Station', id: 'GWR-34' },
  { name: 'Laira Depot', id: 'GWR-35' },
  { name: 'Liskeard Station', id: 'GWR-36' },
  { name: 'London – Capital House', id: 'GWR-37' },
  { name: 'London – Macmillan House', id: 'GWR-38' },
  { name: 'London - The Point', id: 'GWR-39' },
  { name: 'Longrock Depot', id: 'GWR-40' },
  { name: 'Maidenhead Station', id: 'GWR-41' },
  { name: 'Moreton-in-Marsh Station', id: 'GWR-42' },
  { name: 'Nailsea Backwell Station', id: 'GWR-43' },
  { name: 'Newbury Station', id: 'GWR-44' },
  { name: 'Newport Station', id: 'GWR-45' },
  { name: 'Newton Abbot Station', id: 'GWR-46' },
  { name: 'North Camp Station', id: 'GWR-47' },
  { name: 'North Pole IEP Depot', id: 'GWR-48' },
  { name: 'Occupational Health Rdg', id: 'GWR-49' },
  { name: 'Oldfield Park Station', id: 'GWR-50' },
  { name: 'Oxford Station', id: 'GWR-51' },
  { name: 'Oxford Traincrew Depot', id: 'GWR-52' },
  { name: 'Paddington Station', id: 'GWR-53' },
  { name: 'Paignton Station', id: 'GWR-54' },
  { name: 'Pangbourne Station', id: 'GWR-55' },
  { name: 'Par Station', id: 'GWR-56' },
  { name: 'Penzance Station', id: 'GWR-57' },
  { name: 'Pewsey Station', id: 'GWR-58' },
  { name: 'Plymouth Station', id: 'GWR-59' },
  { name: 'Reading Depot', id: 'GWR-60' },
  { name: 'Reading Station', id: 'GWR-61' },
  { name: 'Reading Traincrew Depot', id: 'GWR-62' },
  { name: 'Redhill Station', id: 'GWR-63' },
  { name: 'Redruth Station', id: 'GWR-64' },
  { name: 'Slough Station', id: 'GWR-65' },
  { name: 'St Austell Station', id: 'GWR-66' },
  { name: 'St Erth Station', id: 'GWR-67' },
  { name: 'St Philips Marsh Depot', id: 'GWR-68' },
  { name: 'Stroud Station', id: 'GWR-69' },
  { name: 'Swansea Station', id: 'GWR-70' },
  { name: 'Swindon - Holbrook House', id: 'GWR-71' },
  { name: 'Swindon – Milford House', id: 'GWR-72' },
  { name: 'Swindon – Minton Place', id: 'GWR-73' },
  { name: 'Swindon – Western House', id: 'GWR-74' },
  { name: 'Swindon Station', id: 'GWR-75' },
  { name: 'Taunton Station', id: 'GWR-76' },
  { name: 'Teignmouth Station', id: 'GWR-77' },
  { name: 'Thatcham Station', id: 'GWR-78' },
  { name: 'Theale Station', id: 'GWR-79' },
  { name: 'Tilehurst Station', id: 'GWR-80' },
  { name: 'Tiverton Parkway Station', id: 'GWR-81' },
  { name: 'Torquay Station', id: 'GWR-82' },
  { name: 'Totnes Station', id: 'GWR-83' },
  { name: 'Trowbridge Station', id: 'GWR-84' },
  { name: 'Truro Station', id: 'GWR-85' },
  { name: 'Twyford Station', id: 'GWR-86' },
  { name: 'Warminster Station', id: 'GWR-87' },
  { name: 'West Ealing Station', id: 'GWR-88' },
  { name: 'Westbury Station', id: 'GWR-89' },
  { name: 'Weston-super-Mare Station', id: 'GWR-90' },
  { name: 'Weymouth Station', id: 'GWR-91' },
  { name: 'Windsor & Eton Station', id: 'GWR-92' },
  { name: 'Worcestershire Parkway', id: 'GWR-93' },
  { name: 'Worcs Shrub Hill Station', id: 'GWR-94' },
  { name: 'Yate Station', id: 'GWR-95' },
  { name: 'Yeovil Pen Mill Station', id: 'GWR-96' },
];
</script>

<style lang="scss">
.location-select {
  .option-container {
    .option-title,
    .option-description {
      display: block;
    }
    .option-title {
      color: #333;
      font-size: 14px;
    }
    .option-description {
      color: #666;
      font-size: 12px;

      &.remove-label {
        color: #d63031;
        font-weight: bold;
      }
    }
  }
  .multiselect__placeholder {
    margin: 0 !important;
    padding: 0 !important;
    color: #6c757d;
  }
  .multiselect__single {
    font-size: 1rem !important;
    line-height: 1.5;
    margin: 0 !important;
    padding: 0 !important;
  }
  .multiselect__select {
    display: none !important;
  }
  #locationSearch {
    font-size: 1rem !important;
    line-height: 1.5;
    margin: 0 !important;
    padding: 0 !important;
  }
  .multiselect__option--highlight {
    background: #f2f2f2;
    outline: none;
  }
  &.multiselect-rlnx {
    .multiselect__tags {
      height: auto;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/misc/LocationSearch.vue